import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Users, Briefcase, UserPlus, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ clients: [], leads: [], workers: [] });
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search function
  useEffect(() => {
    if (query.length < 2) {
      setResults({ clients: [], leads: [], workers: [] });
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('portal_token');
        const headers = { Authorization: `Bearer ${token}` };

        const [clientsRes, leadsRes, workersRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/clients/search?q=${encodeURIComponent(query)}&limit=5`, { headers }),
          axios.get(`${API_URL}/api/leads-v2/search?q=${encodeURIComponent(query)}&limit=5`, { headers }),
          axios.get(`${API_URL}/api/workers/search?q=${encodeURIComponent(query)}&limit=5`, { headers })
        ]);

        setResults({
          clients: clientsRes.status === 'fulfilled' ? (clientsRes.value.data.clients || clientsRes.value.data || []) : [],
          leads: leadsRes.status === 'fulfilled' ? (leadsRes.value.data.leads || leadsRes.value.data || []) : [],
          workers: workersRes.status === 'fulfilled' ? (workersRes.value.data.workers || workersRes.value.data || []) : []
        });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleResultClick = (type, item) => {
    setIsOpen(false);
    setQuery('');
    
    switch (type) {
      case 'client':
        navigate(`/admin/crm/${item.client_id}`);
        break;
      case 'lead':
        navigate(`/admin/sales-pipeline?lead=${item.lead_id}`);
        break;
      case 'worker':
        navigate(`/admin/team/management?worker=${item.worker_id}`);
        break;
      default:
        break;
    }
  };

  const totalResults = results.clients.length + results.leads.length + results.workers.length;

  return (
    <div ref={containerRef} className="relative">
      {/* Search Trigger Button */}
      <button
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 100); }}
        className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4 text-[#AEAEB2]" />
        <span className="text-sm text-[#AEAEB2] hidden md:inline">Buscar...</span>
        <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 rounded text-xs text-[#AEAEB2]">
          <span>⌘</span>K
        </kbd>
      </button>

      {/* Search Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/50">
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Search Input */}
            <div className="flex items-center gap-3 p-4 border-b">
              <Search className="w-5 h-5 text-[#AEAEB2]" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar clientes, leads, trabajadores..."
                className="flex-1 text-lg outline-none"
                data-testid="global-search-input"
              />
              {loading && <Loader2 className="w-5 h-5 text-[#AEAEB2] animate-spin" />}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-[#AEAEB2]" />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto">
              {query.length < 2 ? (
                <div className="p-6 text-center text-[#AEAEB2]">
                  Escribe al menos 2 caracteres para buscar
                </div>
              ) : totalResults === 0 && !loading ? (
                <div className="p-6 text-center text-[#AEAEB2]">
                  No se encontraron resultados para "{query}"
                </div>
              ) : (
                <>
                  {/* Clients */}
                  {results.clients.length > 0 && (
                    <div className="p-2">
                      <p className="px-3 py-1 text-xs font-semibold text-[#AEAEB2] uppercase">Clientes</p>
                      {results.clients.map((client) => (
                        <button
                          key={client.client_id}
                          onClick={() => handleResultClick('client', client)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg text-left"
                        >
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Users className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{client.name}</p>
                            <p className="text-sm text-[#AEAEB2]">{client.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Leads */}
                  {results.leads.length > 0 && (
                    <div className="p-2 border-t">
                      <p className="px-3 py-1 text-xs font-semibold text-[#AEAEB2] uppercase">Leads</p>
                      {results.leads.map((lead) => (
                        <button
                          key={lead.lead_id}
                          onClick={() => handleResultClick('lead', lead)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg text-left"
                        >
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <UserPlus className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{lead.name || lead.contact_name || lead.business_name}</p>
                            <p className="text-sm text-[#AEAEB2]">{lead.email || lead.phone || 'Sin contacto'}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Workers */}
                  {results.workers.length > 0 && (
                    <div className="p-2 border-t">
                      <p className="px-3 py-1 text-xs font-semibold text-[#AEAEB2] uppercase">Trabajadores</p>
                      {results.workers.map((worker) => (
                        <button
                          key={worker.worker_id}
                          onClick={() => handleResultClick('worker', worker)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg text-left"
                        >
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Briefcase className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{worker.name}</p>
                            <p className="text-sm text-[#AEAEB2]">{worker.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 border-t text-center">
              <p className="text-xs text-[#AEAEB2]">
                <kbd className="px-1 py-0.5 bg-gray-200 rounded">↑↓</kbd> para navegar
                <span className="mx-2">•</span>
                <kbd className="px-1 py-0.5 bg-gray-200 rounded">Enter</kbd> para seleccionar
                <span className="mx-2">•</span>
                <kbd className="px-1 py-0.5 bg-gray-200 rounded">Esc</kbd> para cerrar
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
